export type ComposeUiUnsupportedReason = 'webgl2' | 'wasmGc' | 'webgl2AndWasmGc'

export type ComposeUiSupport =
  | { supported: true }
  | { supported: false; reason: ComposeUiUnsupportedReason }

const OVERRIDE_KEY = 'ff:composeUiUnsupported'

const REASONS: readonly ComposeUiUnsupportedReason[] = [
  'webgl2',
  'wasmGc',
  'webgl2AndWasmGc',
]

// `(module (type (struct (field i8))))`: only an engine with WasmGC accepts a struct type.
const WASM_GC_PROBE = new Uint8Array([
  0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 95, 1, 120, 0,
])

let cached: ComposeUiSupport | undefined

function hasWebGl2(): boolean {
  try {
    const gl = document.createElement('canvas').getContext('webgl2')
    // Released now: browsers cap live contexts and evict the oldest, which is Compose's.
    gl?.getExtension('WEBGL_lose_context')?.loseContext()
    return Boolean(gl)
  } catch {
    // Some hardened configurations throw instead of returning null.
    return false
  }
}

function hasWasmGc(): boolean {
  try {
    return WebAssembly.validate(WASM_GC_PROBE)
  } catch {
    return false
  }
}

function reasonFor(
  webgl2: boolean,
  wasmGc: boolean,
): ComposeUiUnsupportedReason | null {
  if (!webgl2 && !wasmGc) return 'webgl2AndWasmGc'
  if (!webgl2) return 'webgl2'
  if (!wasmGc) return 'wasmGc'
  return null
}

// Dev-only: preview the unsupported state on a browser that has everything.
function devOverride(): ComposeUiSupport | undefined {
  if (!import.meta.env.DEV) return undefined
  try {
    const raw = localStorage
      .getItem(OVERRIDE_KEY)
      ?.trim()
      .replace(/^["']|["']$/g, '')
    const reason = REASONS.find((r) => r === raw)
    return reason ? { supported: false, reason } : undefined
  } catch {
    return undefined
  }
}

/**
 * Whether this browser can run the shared Compose UI bundle: Skiko needs WebGL2, `wasmJs` needs
 * WasmGC. Logic-only KMP needs neither — never gate it on this.
 */
export function composeUiSupport(): ComposeUiSupport {
  const forced = devOverride()
  if (forced) return forced
  if (!cached) {
    const reason = reasonFor(hasWebGl2(), hasWasmGc())
    cached = reason ? { supported: false, reason } : { supported: true }
  }
  return cached
}

/** Tests only: the probe is a per-load constant everywhere else. */
export function resetComposeUiSupportForTests() {
  cached = undefined
}
